import type { SavedListRecord } from '$lib/api/savedlists';

/**
 * Turning a saved list into the selection it stands for.
 *
 * # Why this is not `records.filter(kind === 'company')`
 *
 * Every record names a company: a person's record names theirs. So a list of
 * people is a list of companies, and it took two attempts to say that in the
 * right place. Filtering for the company records meant a list built by picking
 * people off profiles had nothing to seat; storing the employer alongside each
 * person fixed that and put a row in the list the customer had not chosen.
 * Deriving it costs nothing and leaves the list saying what was actually
 * picked.
 *
 * # The people are the point
 *
 * They ride along as contact identifiers on their company, which is what the
 * data view's rows, the price and an order are all built from. Dropping them
 * made an export of a list of people deliver the companies with nobody in them.
 */

/** One company as a selection seats it. */
export interface ListedCompany {
  id: string;
  name: string;

  /** The people chosen at this company, in the order the list names them. */
  contactIDs: string[];
}

export interface ListSelection {
  companies: ListedCompany[];

  /**
   * How many people the corpus no longer holds.
   *
   * `in_corpus: false` is not a broken record — it is keyed on who somebody is
   * and carries its own snapshot — but there is no live row to deliver, so they
   * cannot be bought. Their company still comes, because the record still names
   * it. Counted so a screen can say so rather than letting a quietly shorter
   * export be discovered on opening the file.
   */
  lost: number;
}

export function companiesFromRecords(records: SavedListRecord[]): ListSelection {
  const byCompany = new Map<string, ListedCompany>();
  let lost = 0;

  for (const record of records) {
    if (!record.company_id) continue;

    let held = byCompany.get(record.company_id);
    if (!held) {
      held = { id: record.company_id, name: record.company_name ?? '', contactIDs: [] };
      byCompany.set(record.company_id, held);
    }

    // The first name wins, so a later record with an empty one cannot blank a
    // company that has already been named.
    if (!held.name && record.company_name) held.name = record.company_name;

    if (record.kind !== 'contact') continue;

    if (record.in_corpus && record.contact_id) {
      held.contactIDs.push(record.contact_id);
    } else {
      lost++;
    }
  }

  return { companies: Array.from(byCompany.values()), lost };
}
